import type { Area } from 'react-easy-crop';

import { translateFile } from '@/i18n';

const TO_RADIANS = Math.PI / 180;

const createImage = (url: string): Promise<HTMLImageElement> =>
  new Promise((resolve, reject) => {
    const image = new Image();
    image.addEventListener('load', () => resolve(image));
    image.addEventListener('error', reject);
    image.setAttribute('crossOrigin', 'anonymous');
    image.src = url;
  });

const getRadianAngle = (degreeValue: number) => degreeValue * TO_RADIANS;

const rotateSize = (width: number, height: number, rotation: number) => {
  const rotRad = getRadianAngle(rotation);
  return {
    width: Math.abs(Math.cos(rotRad) * width) + Math.abs(Math.sin(rotRad) * height),
    height: Math.abs(Math.sin(rotRad) * width) + Math.abs(Math.cos(rotRad) * height),
  };
};

const mimeExtension: Record<string, string> = {
  'image/jpeg': 'jpg',
  'image/png': 'png',
  'image/webp': 'webp',
};

const deriveOutputMimeType = (src: string) => {
  const extension = src.split('.').pop();
  if (extension === 'jpg' || extension === 'jpeg') return 'image/jpeg';
  if (extension === 'png') return 'image/png';
  if (extension === 'webp') return 'image/webp';
  return 'image/jpeg';
};

export async function getCroppedImageFile({
  imageSrc,
  pixelCrop,
  rotation = 0,
  maxOutputSize = 1024,
  quality = 0.84,
  outputMimeType,
}: {
  imageSrc: string;
  pixelCrop: Area;
  rotation?: number;
  maxOutputSize?: number;
  quality?: number;
  outputMimeType?: string;
}): Promise<File> {
  const resolvedMimeType = outputMimeType ?? deriveOutputMimeType(imageSrc);
  const image = await createImage(imageSrc);
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');

  if (!ctx) {
    throw new Error('Canvas not supported');
  }

  const rotRad = getRadianAngle(rotation);
  const { width: bBoxWidth, height: bBoxHeight } = rotateSize(image.width, image.height, rotation);

  canvas.width = bBoxWidth;
  canvas.height = bBoxHeight;

  ctx.translate(bBoxWidth / 2, bBoxHeight / 2);
  ctx.rotate(rotRad);
  ctx.translate(-image.width / 2, -image.height / 2);
  ctx.drawImage(image, 0, 0);

  const croppedCanvas = document.createElement('canvas');
  const croppedCtx = croppedCanvas.getContext('2d');

  if (!croppedCtx) {
    throw new Error('Canvas not supported');
  }

  const maxCropEdge = Math.max(pixelCrop.width, pixelCrop.height);
  const scale = maxCropEdge > maxOutputSize ? maxOutputSize / maxCropEdge : 1;
  const outputWidth = Math.max(1, Math.round(pixelCrop.width * scale));
  const outputHeight = Math.max(1, Math.round(pixelCrop.height * scale));

  croppedCanvas.width = outputWidth;
  croppedCanvas.height = outputHeight;

  croppedCtx.drawImage(
    canvas,
    pixelCrop.x,
    pixelCrop.y,
    pixelCrop.width,
    pixelCrop.height,
    0,
    0,
    outputWidth,
    outputHeight,
  );

  const blob = await new Promise<Blob>((resolve, reject) => {
    croppedCanvas.toBlob(
      (result) => {
        if (!result) {
          reject(new Error('Failed to create image blob'));
          return;
        }
        resolve(result);
      },
      resolvedMimeType,
      resolvedMimeType === 'image/png' ? undefined : quality,
    );
  });

  const extension = mimeExtension[resolvedMimeType] ?? 'jpg';
  const fileName = `cropped-image.${extension}`;

  return new File([blob], fileName, { type: resolvedMimeType });
}

export function validateImageCropFile(
  file: File,
  {
    acceptedMimeTypes,
    maxFileSizeBytes,
    invalidFileMessage,
  }: {
    acceptedMimeTypes: string[];
    maxFileSizeBytes: number;
    invalidFileMessage: string;
  },
): string | null {
  if (!acceptedMimeTypes.includes(file.type)) {
    return invalidFileMessage;
  }

  if (file.size > maxFileSizeBytes) {
    return translateFile('utils.image', 'fileTooLarge');
  }

  return null;
}
